import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
	static targets = ["input", "results"];
	static values = { url: String, clickUrl: String };

	connect() {
		this.activeIndex = -1;
		this.requestId = 0;
		this.optionId = 0;
		this.currentQuery = "";
		document.addEventListener("turbo:before-cache", this.closeBeforeCache);
	}

	disconnect() {
		clearTimeout(this.timeout);
		clearTimeout(this.closeTimeout);
		this.requestId += 1;
		this.abortController?.abort();
		document.removeEventListener("turbo:before-cache", this.closeBeforeCache);
	}

	get options() {
		return Array.from(this.resultsTarget.querySelectorAll("[role='option']"));
	}

	search() {
		clearTimeout(this.timeout);
		const query = this.inputTarget.value.trim();
		this.close();
		if (query.length < 2) return;

		this.timeout = setTimeout(() => this.fetchSuggestions(query), 250);
	}

	async fetchSuggestions(query) {
		this.currentQuery = query;
		this.abortController?.abort();
		const requestId = ++this.requestId;
		this.abortController = new AbortController();
		const separator = this.urlValue.includes("?") ? "&" : "?";
		const url = `${this.urlValue}${separator}${new URLSearchParams({ q: query })}`;

		try {
			const response = await fetch(url, {
				headers: { Accept: "application/json" },
				signal: this.abortController.signal,
			});
			if (requestId !== this.requestId) return;
			if (!response.ok) {
				this.close();
				return;
			}

			const { suggestions = [] } = await response.json();
			if (requestId !== this.requestId) return;
			this.render(suggestions);
		} catch (_) {
			if (requestId === this.requestId) this.close();
		}
	}

	render(suggestions) {
		const options = suggestions.map((suggestion, index) => this.buildOption(suggestion, index + 1, suggestions.length));
		if (options.length === 0) {
			this.close();
			return;
		}
		this.resultsTarget.replaceChildren(...options);
		this.activeIndex = -1;
		this.inputTarget.setAttribute("aria-controls", this.resultsTarget.id);
		this.inputTarget.setAttribute("aria-expanded", "true");
		this.inputTarget.removeAttribute("aria-activedescendant");
		this.resultsTarget.hidden = false;
	}

	buildOption(suggestion, position, count) {
		const option = document.createElement("button");
		option.type = "button";
		option.tabIndex = -1;
		option.id = `${this.resultsTarget.id}-option-${++this.optionId}`;
		option.setAttribute("role", "option");
		option.setAttribute("aria-selected", "false");
		option.className =
			"w-full px-2 py-1.5 text-left hover:bg-cinza-100 dark:hover:bg-surface-sunken aria-selected:bg-cinza-100 dark:aria-selected:bg-surface-sunken";
		option.dataset.position = String(position);
		option.dataset.count = String(count);
		option.dataset.url = suggestion.url;
		option.dataset.searchableType = suggestion.searchable_type;
		option.dataset.searchableId = suggestion.searchable_id;
		this.appendOptionText(option, suggestion.title, [suggestion.type, suggestion.detail]);

		return option;
	}

	appendOptionText(option, title, metadata) {
		const titleElement = document.createElement("span");
		titleElement.className = "block font-medium text-ink";
		titleElement.textContent = title;

		const metadataElement = document.createElement("span");
		metadataElement.className = "flex flex-wrap gap-x-2 gap-y-1 text-sm text-cinza-600 dark:text-ink-muted";
		for (const text of metadata) {
			if (!text) continue;

			const detailElement = document.createElement("span");
			detailElement.textContent = text;
			metadataElement.append(detailElement);
		}

		option.append(titleElement, metadataElement);
	}

	navigate(event) {
		const options = this.options;

		if (event.key === "Escape") {
			if (options.length === 0) return;
			event.preventDefault();
			event.stopPropagation();
			this.close();
			return;
		}

		if (options.length === 0) return;

		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			event.preventDefault();
			const step = event.key === "ArrowDown" ? 1 : -1;
			this.activeIndex = (this.activeIndex + step + options.length) % options.length;
			this.highlight(options);
		} else if (event.key === "Enter" && this.activeIndex >= 0) {
			event.preventDefault();
			this.select(options[this.activeIndex]);
		}
	}

	highlight(options = this.options) {
		options.forEach((option, index) => {
			const active = index === this.activeIndex;
			option.setAttribute("aria-selected", String(active));
			if (active) option.scrollIntoView({ block: "nearest" });
		});

		if (this.activeIndex >= 0) {
			this.inputTarget.setAttribute("aria-activedescendant", options[this.activeIndex].id);
		} else {
			this.inputTarget.removeAttribute("aria-activedescendant");
		}
	}

	scheduleClose() {
		clearTimeout(this.closeTimeout);
		this.closeTimeout = setTimeout(() => this.close(), 100);
	}

	preserveSelection(event) {
		event.preventDefault();
	}

	pick(event) {
		const option = event.target.closest("[role='option']");
		if (option) this.select(option);
	}

	select(option) {
		this.logClick(option);
		Turbo.visit(option.dataset.url);
		this.close();
	}

	// Fire-and-forget beacon: an autocomplete pick never visits /buscar, so this
	// carries the typed query and shown-suggestion count for the server to log its
	// own SearchQuery (source: autocomplete) alongside the SearchClick. section is
	// always "autocomplete" here — this is the only place picks like this happen,
	// unlike SearchResultComponent's two lists.
	logClick(option) {
		if (!this.clickUrlValue) return;

		const data = new FormData();
		data.append("searchable_type", option.dataset.searchableType);
		data.append("searchable_id", option.dataset.searchableId);
		data.append("position", option.dataset.position);
		data.append("section", "autocomplete");
		data.append("query", this.currentQuery);
		data.append("results_count", option.dataset.count);

		navigator.sendBeacon(this.clickUrlValue, data);
	}

	close() {
		clearTimeout(this.timeout);
		clearTimeout(this.closeTimeout);
		this.requestId += 1;
		this.abortController?.abort();
		this.abortController = undefined;
		this.resultsTarget.replaceChildren();
		this.resultsTarget.hidden = true;
		this.activeIndex = -1;
		this.inputTarget.setAttribute("aria-expanded", "false");
		this.inputTarget.removeAttribute("aria-activedescendant");
	}

	closeBeforeCache = () => {
		this.close();
	};
}
